import * as Sentry from '@sentry/react';
import WebApp from '@twa-dev/sdk';
import { nanoid } from 'nanoid';

import { type InitialState } from '@/app/scripts/init-application/types';

import { getToken } from '@/shared/api/auth';
import { BASE_URL_MARKET, DEV_TG_BOT_LINK, PROD_TG_BOT_LINK } from '@/shared/constants';
import { getDecodedStartappQuery, getUIKit, isDev, parseJwt } from '@/shared/helpers';
import { removeCookie } from '@/shared/helpers/remove-cookie';
import { getAppLocale } from '@/shared/services/localization';
import { initI18n } from '@/shared/services/localization/i18n';
import { logErrorMini } from '@/shared/services/service-logger/lib/log-error-mini';

import { PageWrapperStyle, ThemeMode } from '../../shared/store/app-store/types';
import { StartAppUtil } from '../../shared/store/tg-store/types';

export const getInitialAppState = async (): Promise<InitialState> => {
  try {
    if (!WebApp.initData && WebApp.platform !== 'unknown') {
      // Reopening the page if initData is missing.
      // openTelegramLink generates initData, so it will work.
      // Unfortunately, Android doesn’t allow opening the same web app that is currently open.

      const resolveTgBotLink =
        (isDev('replyBot') ? DEV_TG_BOT_LINK : PROD_TG_BOT_LINK) + BASE_URL_MARKET;

      if (WebApp.platform !== 'android') {
        WebApp.openTelegramLink(resolveTgBotLink);
        WebApp.close();
      }
    }

    if (Sentry.isInitialized()) {
      Sentry.setTag('platform', WebApp.platform);

      const user = WebApp.initDataUnsafe?.user;

      if (user) {
        Sentry.setUser({
          id: user.id,
          username: user.username,
        });
      }
    }

    const startAppArgs = getDecodedStartappQuery();

    if (startAppArgs.util === StartAppUtil.RESET) {
      removeCookie('token');
    }

    const initURLHash =
      (window.location.hash.slice(1) || localStorage.getItem('dev-hash')) ?? '';
    const token = await getToken(initURLHash);
    const parsedToken = parseJwt(token);

    const lang = await getAppLocale();

    const uiKit = getUIKit();
    const theme = (WebApp?.colorScheme as ThemeMode) || ThemeMode.DARK;

    const pageWrapperStyle = PageWrapperStyle.PRIMARY;

    const marketSessionId = nanoid();

    const i18n = await initI18n();

    const state = {
      app: {
        lang,
        token,
        parsedToken,
        theme,
        uiKit,
        pageWrapperStyle,
        marketSessionId,
        wsSentInitialMsg: false,
        i18n,
      },
      tg: {
        startAppArgs,
        tg: WebApp,
        initURLHash,
        isFullscreen: !!WebApp?.isFullscreen,
        backButton: { customAction: null },
      },
    };

    return state;
  } catch (error) {
    logErrorMini(error as Error, 'getInitialAppState', {
      stage: 'initialization',
      tgAvailable: String(!!WebApp),
    });
    throw error;
  }
};
